// Paket S8 — MembershipOnboardingHost.
//
// Nach bestaetigtem Kauf oder Trial-Start zeigt der Host einen freundlichen
// Einstiegsdialog ("Womit moechtest du anfangen?") mit drei Karten:
//   • writing   → letztes aktives Abenteuer (oder Charakter-Chat), sonst Dashboard
//   • listening → Vorlesen im letzten Abenteuer (oder Lagerfeuer bei Ultra)
//   • design    → Einstellungen > Erscheinungsbild
//
// Dazu eine Vorteilswoche (max. 1 Banner je App-Sitzung, nie zusammen mit
// einem Promo-Dialog) und die optionale Trial-Erinnerung 48 h vor der ersten
// Abbuchung. Keine Push-Benachrichtigungen, kein Spam — alles ist
// ueberspringbar.
//
// Aenderungen F5s/P2-3:
//   • Skip wird serverseitig als 'skip'-Sentinel persistiert (vorher null).
//   • Snapshot wird via `getPromoState` (apiRequest) geladen, damit Bearer-
//     Token und VITE_API_BASE korrekt greifen (vorher roher fetch).
//   • Solange der Snapshot laedt, wird weder Dialog noch Banner gezeigt.
//   • Dialog und Banner koennen nicht gleichzeitig erscheinen.
//   • "Jetzt entdecken" fuehrt zu einem konkreten, vorhandenen Ziel.
// Datenquelle: GET /api/membership/promo liefert `state.onboarding_*`,
// `onboarding { step, choice, next_benefit, skipped }` und `trial_reminder
// { due, ends_at }`. Persistenz: POST /onboarding-choice (writing/listening/
// design/skip + payment_reminder_opt_in) und POST /trial-reminder-ack
// (shown/dismiss/manage).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ResponsiveDialog } from '../components/ui/ResponsiveDialog';
import { useT } from '../i18n';
import { useMembership } from '../hooks/useMembership';
import { useAuth } from '../auth/AuthProvider';
import { queryKeys } from '../api/queryKeys';
import {
  postOnboardingChoice,
  postTrialReminderAck,
  type OnboardingChoice,
  type OnboardingInfo,
  type TrialReminderInfo,
} from '../api/promo';
import { trackFunnel } from '../api/funnel';
import { useStartupGate } from './StartupGateCoordinator';
import { usePromo } from '../promo/PromoProvider';
import { isExcludedRoute } from '../promo/promoClientRules';
import { useLocation } from 'react-router-dom';
import styles from './MembershipOnboardingHost.module.css';

function readSessionStorage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.sessionStorage; }
  catch { return null; }
}

const ONBOARDING_SHOWN_SESSION_KEY = 'lorath_onboarding_choice_shown';
const ONBOARDING_BANNER_SHOWN_SESSION_KEY = 'lorath_onboarding_banner_shown';

const MEMBER_SEGMENTS = new Set(['plus', 'ultra', 'plus_trial']);

type BenefitKey = 'writing' | 'listening' | 'design';

interface HostState {
  loaded: boolean;
  onboardingChoice: BenefitKey | 'skip' | null;
  onboardingStep: number;
  onboardingStartedAt: string | null;
  paymentReminderOptIn: boolean;
  nextBenefit: BenefitKey | null;
  stepFromServer: number;
  skipped: boolean;
  trialDue: boolean;
  trialEndsAt: string | null;
}

const INITIAL_STATE: HostState = {
  loaded: false,
  onboardingChoice: null,
  onboardingStep: 0,
  onboardingStartedAt: null,
  paymentReminderOptIn: false,
  nextBenefit: null,
  stepFromServer: 0,
  skipped: false,
  trialDue: false,
  trialEndsAt: null,
};

function formatDateDE(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Liefert den Tiefenlink zum konkreten Vorteil. Prioritaet:
 *   1. writing    → Dashboard (zeigt das letzte aktive Abenteuer oben)
 *   2. listening  → Soundboard (Lagerfeuer) bzw. Audiobook-Liste
 *   3. design     → Einstellungen > Erscheinungsbild (/settings/appearance)
 * Wer keinen echten Vorteil hat, bekommt die jeweilige Einstiegsseite.
 */
function benefitRoute(benefit: BenefitKey, navigate: (path: string) => void) {
  if (benefit === 'design') {
    navigate('/settings/appearance');
    return;
  }
  if (benefit === 'listening') {
    // Soundboard ist der Einstieg ins Lagerfeuer; ohne aktives Audiobook
    // hat der Nutzer dort sofort einen konkreten Anlaufpunkt.
    navigate('/soundboard');
    return;
  }
  // writing: konkretes letztes Abenteuer oder Charakter-Chat. Ohne
  // eigenen API-Call springen wir auf das Dashboard; das Dashboard
  // rendert ohnehin den zuletzt aktiven Eintrag oben.
  navigate('/dashboard');
}

export function MembershipOnboardingHost() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const gate = useStartupGate();
  const membership = useMembership();
  const queryClient = useQueryClient();
  // S-33 (7.3.3.3, Befund J-01): vorher holte dieser Host GET /api/membership/promo
  // über einen eigenen apiRequest-Aufruf, obwohl PromoProvider denselben
  // Endpunkt schon per React-Query lädt (beide gleichzeitig unter App.tsx
  // gemountet) — bei jedem authentifizierten App-Start ging der Request also
  // doppelt raus. Jetzt wird derselbe Cache-Eintrag gelesen (usePromo()),
  // kein eigener Netzaufruf mehr.
  const { snapshot: promoSnapshot, snapshotLoaded: promoSnapshotLoaded } = usePromo();

  const isMember = MEMBER_SEGMENTS.has(membership.tier === 'founders' || membership.tier === 'torchbearer'
    ? 'none' : membership.tier);

  const routeExcluded = isExcludedRoute(location.pathname);

  // Dialog-State (Einstiegswahl): "choice" = welcher Vorteil gewaehlt wurde,
  // "step" = welcher Vorteilswochen-Banner als naechstes faellig ist.
  const [dialogChoice, setDialogChoice] = useState<null | OnboardingChoice>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showBanner, setShowBanner] = useState<null | BenefitKey>(null);
  const [showReminder, setShowReminder] = useState(false);
  const [optIn, setOptIn] = useState(true);

  const [state, setState] = useState<HostState>(INITIAL_STATE);

  const sessionShownDialogRef = useRef(false);
  const sessionShownBannerRef = useRef(false);
  // Nachbesserung (Review FIX2-A, 23.09.2026): der geteilte 'promo'-Query-
  // Cache wird app-weit von vielen unabhaengigen Stellen invalidiert
  // (PromoProvider.decide() bei praktisch jedem Promo-Funnel-Ereignis,
  // ackAction, ackIntroSeen, Admin-Preview-Toggle, Kauf-Flow) — nicht nur
  // durch handleSelect/handleSkip/acceptBanner hier. Ein Refetch, der VOR
  // einem dieser optimistischen Schreibvorgaenge gestartet wurde, kann mit
  // noch-altem Stand (onboarding_choice=null) NACH dem optimistischen
  // setState ankommen und ihn kurz zuruecksetzen. suppressSyncRef haelt den
  // Sync-Effekt waehrend eines solchen Schreibvorgangs an; freigegeben wird
  // erst wieder, nachdem WIR selbst den Snapshot per invalidateQueries neu
  // geholt haben (garantiert frisch, weil das erst NACH dem erfolgreichen
  // POST passiert).
  const suppressSyncRef = useRef(false);

  // Übernimmt den geteilten Snapshot aus PromoProvider (S-33) statt selbst zu
  // fetchen. Rein lesende Ableitung — kein Netzaufruf hier. `state` bleibt
  // trotzdem ein eigenes useState, weil handleSelect/handleSkip/acceptBanner
  // ihn danach optimistisch weiterschreiben (Sofort-Feedback, bevor ein
  // erneuter Snapshot ankommt).
  useEffect(() => {
    if (auth.status !== 'authed') return;
    if (!promoSnapshotLoaded) return;
    if (suppressSyncRef.current) return;
    if (!promoSnapshot) {
      // Fehlerfall (Provider konnte nicht laden): als geladen markieren,
      // damit kein endloser "Bereite Vorteile vor"-Zustand haengen bleibt.
      setState((s) => (s.loaded ? s : { ...s, loaded: true }));
      return;
    }
    const data = promoSnapshot;
    const choice = data?.state?.onboarding_choice ?? null;
    const step = Number(data?.state?.onboarding_step ?? 0);
    const startedAt = data?.state?.onboarding_started_at ?? null;
    const opt = !!data?.state?.payment_reminder_opt_in;
    const ob = (data?.onboarding || {}) as Partial<OnboardingInfo>;
    const tr = (data?.trial_reminder || {}) as Partial<TrialReminderInfo>;
    setState({
      loaded: true,
      onboardingChoice: (choice === 'writing' || choice === 'listening' || choice === 'design'
        ? choice
        : choice === 'skip'
          ? 'skip'
          : null),
      onboardingStep: step,
      onboardingStartedAt: startedAt,
      paymentReminderOptIn: opt,
      nextBenefit: (ob.next_benefit === 'writing' || ob.next_benefit === 'listening' || ob.next_benefit === 'design'
        ? ob.next_benefit
        : null),
      stepFromServer: Number(ob.step ?? step),
      skipped: !!ob.skipped || choice === 'skip',
      trialDue: !!tr.due,
      trialEndsAt: tr.ends_at ?? null,
    });
  }, [auth.status, promoSnapshot, promoSnapshotLoaded]);

  // Opt-in Default: an, ausser Nutzer hat es explizit abgelehnt.
  useEffect(() => {
    setOptIn(state.paymentReminderOptIn || (!state.onboardingChoice && state.paymentReminderOptIn === false ? false : true));
  }, [state.paymentReminderOptIn, state.onboardingChoice]);

  // Wenn der Nutzer Mitglied geworden ist UND noch keine Wahl getroffen
  // hat UND wir den Dialog in dieser Sitzung noch nicht gezeigt haben →
  // Dialog oeffnen. Bei "skip" wird choice='skip' gespeichert, sodass wir
  // NICHT erneut fragen. Bei Trial (plus_trial) ebenfalls Dialog zeigen.
  // Trial-Erinnerung hat Vorrang — sie ist zeitkritisch (48h vor erster
  // Abbuchung) und wird in einem eigenen Slot angezeigt.
  useEffect(() => {
    if (!state.loaded) return;
    if (gate !== 'none' || routeExcluded) return;
    if (!isMember) return;
    if (state.skipped || state.onboardingChoice === 'skip') return;
    if (state.onboardingChoice !== null) return;
    if (state.onboardingStartedAt !== null) return;
    if (state.trialDue) return;
    if (sessionShownDialogRef.current) return;
    const ss = readSessionStorage();
    if (ss && ss.getItem(ONBOARDING_SHOWN_SESSION_KEY) === '1') {
      sessionShownDialogRef.current = true;
      return;
    }
    sessionShownDialogRef.current = true;
    try { ss?.setItem(ONBOARDING_SHOWN_SESSION_KEY, '1'); } catch { /* noop */ }
    setShowDialog(true);
  }, [gate, isMember, routeExcluded, state.loaded, state.onboardingChoice, state.onboardingStartedAt, state.skipped, state.trialDue]);

  // Vorteilswochen-Banner: einmalig je Sitzung, nur wenn der Nutzer eine
  // echte Wahl getroffen hat (kein Skip), NIE zusammen mit dem Promo-Dialog
  // oder dem Einstiegsdialog (P2-3), ab Schritt 2 (>= 2 Tage seit Start).
  useEffect(() => {
    if (!state.loaded) return;
    if (gate !== 'none' || routeExcluded) return;
    if (state.skipped) return;
    if (!state.onboardingChoice || state.onboardingChoice === 'skip') return;
    if (!state.nextBenefit) return;
    if (state.stepFromServer < 2) return;
    if (showDialog) return; // niemals gleichzeitig mit dem Einstiegsdialog
    if (sessionShownBannerRef.current) return;
    const ss = readSessionStorage();
    if (ss && ss.getItem(ONBOARDING_BANNER_SHOWN_SESSION_KEY) === '1') {
      sessionShownBannerRef.current = true;
      return;
    }
    sessionShownBannerRef.current = true;
    try { ss?.setItem(ONBOARDING_BANNER_SHOWN_SESSION_KEY, '1'); } catch { /* noop */ }
    setShowBanner(state.nextBenefit);
  }, [gate, routeExcluded, showDialog, state.loaded, state.onboardingChoice, state.nextBenefit, state.skipped, state.stepFromServer]);

  // Trial-Erinnerung: nur bei due=true (Server hat Opt-in + Fenster geprueft)
  // und nur einmalig. Der Server-Endpoint entscheidet, ob sie faellig ist.
  const reminderShownRef = useRef(false);
  useEffect(() => {
    if (!state.loaded) return;
    if (gate !== 'none' || routeExcluded) return;
    if (!state.trialDue) return;
    if (reminderShownRef.current) return;
    reminderShownRef.current = true;
    setShowReminder(true);
    void postTrialReminderAck('shown').catch(() => undefined);
  }, [gate, routeExcluded, state.loaded, state.trialDue]);

  const handleSelect = async (choice: Exclude<OnboardingChoice, 'skip'>) => {
    setDialogChoice(choice);
    setShowDialog(false);
    // Sync-Effekt anhalten: siehe Kommentar bei suppressSyncRef oben.
    suppressSyncRef.current = true;
    try {
      const res = await postOnboardingChoice({
        choice,
        payment_reminder_opt_in: optIn,
      });
      setState((s) => ({
        ...s,
        onboardingChoice: choice,
        onboardingStep: 1,
        onboardingStartedAt: new Date().toISOString(),
        paymentReminderOptIn: optIn,
        skipped: false,
      }));
      try { trackFunnel('first_benefit_used', { reason: 'onboarding_selected', meta: { choice, step: res.step } }); }
      catch { /* noop */ }
      // Sync-Sperre erst NACH einem garantiert frischen Snapshot loesen —
      // nicht blockierend, damit die Navigation unten nicht auf den
      // Nachlade-Refetch wartet.
      void queryClient.invalidateQueries({ queryKey: queryKeys.promo.snapshot() })
        .catch(() => undefined)
        .finally(() => { suppressSyncRef.current = false; });
    } catch {
      // Fehler bleiben still — der Nutzer hat gewaehlt, wir koennen es beim
      // naechsten Mount nochmal versuchen.
      suppressSyncRef.current = false;
    }
    // Direkt zum konkreten Vorteil navigieren (P2-3).
    benefitRoute(choice, navigate);
  };

  const handleSkip = async () => {
    setShowDialog(false);
    suppressSyncRef.current = true;
    try {
      await postOnboardingChoice({ choice: 'skip', payment_reminder_opt_in: false });
      setState((s) => ({
        ...s,
        onboardingChoice: 'skip',
        onboardingStep: 0,
        onboardingStartedAt: null,
        paymentReminderOptIn: false,
        skipped: true,
      }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.promo.snapshot() })
        .catch(() => undefined)
        .finally(() => { suppressSyncRef.current = false; });
    } catch {
      suppressSyncRef.current = false;
    }
  };

  const dismissBanner = () => {
    setShowBanner(null);
  };

  const acceptBanner = () => {
    // Schritt hochzaehlen, damit der naechste Banner (Schritt 3) erscheinen
    // kann — wenn der Server ueber stepFromServer bereits 3 liefert, lassen
    // wir ihn auf 3.
    const nextStep = Math.min(3, state.stepFromServer);
    const benefit = showBanner;
    setShowBanner(null);
    if (state.onboardingChoice && state.onboardingChoice !== 'skip') {
      suppressSyncRef.current = true;
      setState((s) => ({ ...s, onboardingStep: nextStep }));
      void postOnboardingChoice({
        choice: state.onboardingChoice,
        step: nextStep,
      })
        .catch(() => undefined)
        .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.promo.snapshot() }))
        .catch(() => undefined)
        .finally(() => { suppressSyncRef.current = false; });
    }
    // "Jetzt entdecken" muss zum echten Vorteil fuehren (P2-3).
    if (benefit) benefitRoute(benefit, navigate);
  };

  const handleReminderDismiss = () => {
    setShowReminder(false);
    void postTrialReminderAck('dismiss').catch(() => undefined);
  };
  const handleReminderManage = () => {
    setShowReminder(false);
    void postTrialReminderAck('manage').catch(() => undefined);
    try {
      navigate('/mitgliedschaft/uebersicht');
    } catch {
      navigate('/mitgliedschaft');
    }
  };

  if (gate !== 'none') return null;

  // Solange der Snapshot laedt, zeigen wir weder Dialog noch Banner —
  // ein "Bereite deine Vorteile vor …"-Hinweis reicht. Verhindert das
  // Aufflackern mit Default-Werten bei langsamer Verbindung (P2-3).
  if (!state.loaded && isMember && !state.skipped && state.onboardingChoice === null) {
    return (
      <div className={styles.loadingHint} data-testid="onboarding-loading" aria-live="polite">
        {t('onboarding.loading')}
      </div>
    );
  }

  return (
    <>
      {showDialog && (
        <ResponsiveDialog
          open
          onClose={handleSkip}
          title={t('onboarding.choice.title')}
          maxWidth={520}
          presentation="modal"
        >
          <p className={styles.dialogIntro}>{t('onboarding.choice.intro')}</p>
          <div className={styles.cardGrid}>
            <button
              type="button"
              className={styles.card}
              onClick={() => handleSelect('writing')}
              data-testid="onboarding-card-writing"
            >
              <span className={styles.cardTitle}>{t('onboarding.choice.writing_title')}</span>
              <span className={styles.cardBody}>{t('onboarding.choice.writing_body')}</span>
            </button>
            <button
              type="button"
              className={styles.card}
              onClick={() => handleSelect('listening')}
              data-testid="onboarding-card-listening"
            >
              <span className={styles.cardTitle}>{t('onboarding.choice.listening_title')}</span>
              <span className={styles.cardBody}>{t('onboarding.choice.listening_body')}</span>
            </button>
            <button
              type="button"
              className={styles.card}
              onClick={() => handleSelect('design')}
              data-testid="onboarding-card-design"
            >
              <span className={styles.cardTitle}>{t('onboarding.choice.design_title')}</span>
              <span className={styles.cardBody}>{t('onboarding.choice.design_body')}</span>
            </button>
          </div>
          <label className={styles.optIn}>
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              data-testid="onboarding-optin"
            />
            <span>{t('onboarding.choice.opt_in')}</span>
          </label>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.later}
              onClick={handleSkip}
              data-testid="onboarding-skip"
            >
              {t('onboarding.choice.skip')}
            </button>
          </div>
        </ResponsiveDialog>
      )}

      {showBanner && !showDialog && (
        <ResponsiveDialog
          open
          onClose={dismissBanner}
          title={t('onboarding.banner.title', { benefit: t(`onboarding.choice.${showBanner}_title`) })}
          maxWidth={460}
          presentation="modal"
        >
          <p>{t('onboarding.banner.text', { benefit: t(`onboarding.choice.${showBanner}_title`) })}</p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              onClick={acceptBanner}
              data-testid="onboarding-banner-accept"
            >
              {t('onboarding.banner.cta')}
            </button>
            <button
              type="button"
              className={styles.later}
              onClick={dismissBanner}
              data-testid="onboarding-banner-dismiss"
            >
              {t('promo.later')}
            </button>
          </div>
        </ResponsiveDialog>
      )}

      {showReminder && !showDialog && !showBanner && (
        <ResponsiveDialog
          open
          onClose={handleReminderDismiss}
          title={t('onboarding.trial_reminder.title')}
          maxWidth={460}
          presentation="modal"
        >
          <p>
            {t('onboarding.trial_reminder.body', {
              date: formatDateDE(state.trialEndsAt, lang),
              price: '',
              duration: '',
            })}
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.later}
              onClick={handleReminderDismiss}
              data-testid="onboarding-reminder-ok"
            >
              {t('onboarding.trial_reminder.ok')}
            </button>
            <button
              type="button"
              className={styles.primary}
              onClick={handleReminderManage}
              data-testid="onboarding-reminder-manage"
            >
              {t('onboarding.trial_reminder.manage')}
            </button>
          </div>
        </ResponsiveDialog>
      )}
    </>
  );
}

// Mount-Zeitpunkt: keine modul-globale Lock-Variable mehr noetig, da das
// Onboarding bewusst KEINER Start-Sperre unterliegt (der Nutzer hat gerade
// ein Abo abgeschlossen und soll sofort einen Vorteil erleben).

export default MembershipOnboardingHost;